import { useNavigate, useSearch } from '@tanstack/react-router';

import { AssignmentGatewayMessage } from '../../../../types/AssignmentGatewayMessage';
import { AssignmentStatus } from '../../../../types/AssignmentMessage';
import { CenteredPagination } from '../../../cdl/Pagination/CenteredPagination';
import { TableBuilder } from '../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../cdl/TableBuilder/TableBuilderColumn';
import { AdvancedError } from '../../../common/Error/AdvancedError';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { formatPort } from '../../../common/helpers/formatPort.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { Characters } from '../../../common/helpers/unicode.helper';
import { usePaymentTermsFormat } from '../../../common/hooks/usePaymentTermsFormat';
import { ListingWithMore } from '../../../common/ListingWithMore/ListingWithMore';
import { LoadingIndicator } from '../../../common/LoadingIndicator/LoadingIndicator';
import { useAssignmentsPagination } from '../hooks/useAssignmentsPagination';

import { AssignmentsDropdown } from './AssignmentsDropdown';
import { AssignmentsEmptyState } from './AssignmentsEmptyState';
import { AssignmentStatusBadge } from './AssignmentStatusBadge';

export const AssignmentsTable = () => {
    const search = useSearch({ from: '/_app/assignments' });
    const navigate = useNavigate({ from: '/assignments' });

    const assignmentsPaginationQuery = useAssignmentsPagination({
        page: search.page,
        supplierIds: search.supplierIds,
        customerIds: search.customerIds,
    });

    const formatPaymentTerms = usePaymentTermsFormat();

    if (assignmentsPaginationQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (assignmentsPaginationQuery.isError) {
        return <AdvancedError />;
    }

    if (assignmentsPaginationQuery.data.content.length === 0) {
        return <AssignmentsEmptyState />;
    }

    return (
        <div>
            <TableBuilder data={assignmentsPaginationQuery.data.content} grow={false}>
                <TableBuilderColumn<AssignmentGatewayMessage>
                    width="15%"
                    header={translate('assignments.customer')}
                    truncate
                >
                    {(row) => formatCompanyName({ company: row.customer })}
                </TableBuilderColumn>

                <TableBuilderColumn<AssignmentGatewayMessage>
                    width="15%"
                    header={translate('assignments.supplier')}
                    truncate
                >
                    {(row) => formatCompanyName({ company: row.supplier })}
                </TableBuilderColumn>

                <TableBuilderColumn<AssignmentGatewayMessage>
                    width="25%"
                    header={translate('assignments.portLimitations')}
                >
                    {(row) => {
                        if (!row.portLimitationsPorts?.length) {
                            return Characters.EN_DASH;
                        }

                        return (
                            <ListingWithMore>
                                {row.portLimitationsPorts.map((port) => formatPort({ port: port }))}
                            </ListingWithMore>
                        );
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<AssignmentGatewayMessage>
                    width="15%"
                    header={translate('assignments.defaultPaymentTermDays')}
                    truncate
                >
                    {(row) => {
                        if (!row.defaultPaymentTermReference) {
                            return Characters.EN_DASH;
                        }

                        return formatPaymentTerms(row.defaultPaymentTermReference, row.defaultPaymentTermReferenceDays);
                    }}
                </TableBuilderColumn>

                <TableBuilderColumn<AssignmentGatewayMessage> width="5%" header={translate('assignments.status')}>
                    {(row) => <AssignmentStatusBadge status={row.status} />}
                </TableBuilderColumn>

                <TableBuilderColumn<AssignmentGatewayMessage> width="5%" header="">
                    {(row) => {
                        if (row.status === AssignmentStatus.REQUESTED) {
                            return null;
                        }

                        return <AssignmentsDropdown assignment={row} />;
                    }}
                </TableBuilderColumn>
            </TableBuilder>

            <CenteredPagination
                currentPage={search.page}
                pageTotal={assignmentsPaginationQuery.pageTotal}
                onPageChange={(page) => {
                    navigate({ search: (previousSearch) => ({ ...previousSearch, page }) });
                }}
            />
        </div>
    );
};
